import { useEffect, useRef, useState } from 'react'

import { useClipFailureTimeout } from './useClipFailureTimeout'
import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'

import { NetworkStatus } from '@apollo/client'

import {
  getCannedIncidentVideoStreamState,
  isIncidentSeeded,
} from '@/features/canned-video'
import { useIncidentVideoQuery } from '@/graphql/generated/hooks'
import { IncidentVideoQuery } from '@/graphql/generated/operations'
import { IncidentType } from '@/graphql/generated/schemas'

import {
  EXTENDED_CLIP_FAILURE_TIMEOUT_MS,
  FOCUSED_CLIP_FAILURE_TIMEOUT_MS,
} from '../constants/clipTimeouts'
import { ClipTimeRange, VideoFeedEvent } from '../types/types'
import { IncidentPlayerError, IncidentVideoStateIProps } from '../types/types'
import { isSameClipUrl } from '../utils/clipUrl'
import { pickFirstExtendedClip, pickResolvedClip } from '../utils/resolveClip'

const incidentTypesWithoutVideo = [
  IncidentType.DeviceHealth,
  IncidentType.DeviceOffline,
]
const VIDEO_PROCESSING_POLL_INTERVAL = 3_000
const VIDEO_POLL_TIMEOUT = 30_000
// The EXTENDED (forensic) clip is generated a few minutes after the incident (ENG-31365), so once
// the resolved clip is playing the query keeps polling at a relaxed cadence until the extended clip
// shows up. The wait is bounded by the incident's age — we give up once the incident is older than
// EXTENDED_CLIP_FAILURE_TIMEOUT_MS (measured from `createdAt`, NOT from when the drawer opened),
// since by then the extended clip either already exists or never will. That is the same threshold
// the Extended tab's error state uses, so polling and the error state flip together.
const EXTENDED_CLIP_POLL_INTERVAL = 15_000
// Every poll re-mints the presigned URLs (no-cache). Swapping the player's source for a re-sign
// of the same clip restarts playback, so the exposed URLs are kept until the clip itself changes
// or the current signature is about to expire.
const URL_SWAP_EXPIRY_MARGIN_MS = 60_000

interface UseIncidentVideoIProps {
  incidentId: string
  incidentType: IncidentType
  hasVideoClip: boolean
  /**
   * Incident creation time (ISO) — anchors the extended-clip give-up window and both clip-failure
   * deadlines.
   */
  createdAt?: string
  onEvent?: (event: VideoFeedEvent) => void
  skip?: boolean
  /** Overrides the focused-clip failure deadline. See constants/clipTimeouts. */
  focusedFailureTimeoutMs?: number
  /** Overrides the extended-clip failure deadline (and its polling give-up window). */
  extendedFailureTimeoutMs?: number
}

export const useIncidentVideo = ({
  incidentId,
  incidentType,
  hasVideoClip,
  createdAt,
  onEvent,
  skip,
  focusedFailureTimeoutMs = FOCUSED_CLIP_FAILURE_TIMEOUT_MS,
  extendedFailureTimeoutMs = EXTENDED_CLIP_FAILURE_TIMEOUT_MS,
}: UseIncidentVideoIProps): IncidentVideoStateIProps => {
  const timeoutRef = useRef(null)
  const extendedPollTimeoutRef = useRef(null)
  const hasGivenUpOnExtendedRef = useRef(false)
  const playingUrlRef = useRef<string | null>(null)
  const playingUrlExpiryRef = useRef(0)
  const playingExtendedUrlRef = useRef<string | null>(null)
  const playingExtendedExpiryRef = useRef(0)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [isProcessing, setIsProcessing] = useState(false)
  const [url, setUrl] = useState(null)
  const [extendedUrl, setExtendedUrl] = useState<string | null>(null)
  const [isExtendedClipPending, setIsExtendedClipPending] = useState(false)
  const [resolvedClipRange, setResolvedClipRange] =
    useState<ClipTimeRange | null>(null)
  const [extendedClipRange, setExtendedClipRange] =
    useState<ClipTimeRange | null>(null)

  const isCannedVideoEnabled = useFeatureFlag(FeatureFlag.cannedVideo)
  const isResolvedClipEnabled = useFeatureFlag(FeatureFlag.resolvedIncidentClip)
  const shouldUseCannedVideo =
    isIncidentSeeded(incidentId) && isCannedVideoEnabled

  // To cancel timeout in edge case where we load drawer and don't have video,
  // then video comes in before timeout, and we don't actually want the timeout to trigger
  let timeoutId: number | null = null

  const handleCompleted = (data: IncidentVideoQuery) => {
    setIsLoading(false)
    // When enabled, play the resolved clip (ORIGINAL/DOWNSCALED, per api-general's resolution
    // order) selected from `Incident.clips`, falling back to `Incident.video` when none is
    // available. The resolved clip's `video` wrapper is shape-compatible with `Incident.video`.
    const resolvedClip = isResolvedClipEnabled
      ? pickResolvedClip(data?.incident?.clips)
      : null
    const resolvedVideo = resolvedClip?.video ?? data?.incident?.video
    // Expose the first EXTENDED (forensic) clip, gated by the same flag, so the player can offer
    // an "Extended" toggle. Null when the flag is off or no extended clip is available.
    const extendedClip = isResolvedClipEnabled
      ? pickFirstExtendedClip(data?.incident?.clips)
      : null
    // Keep the exposed extended URL while it points at the same clip and its signature is still
    // fresh — a re-signed URL for the same object would restart Extended playback.
    const nextExtendedUrl = extendedClip?.video?.url ?? null
    const isExtendedUrlFresh =
      playingExtendedExpiryRef.current - Date.now() > URL_SWAP_EXPIRY_MARGIN_MS
    if (
      !nextExtendedUrl ||
      !isSameClipUrl(playingExtendedUrlRef.current, nextExtendedUrl) ||
      !isExtendedUrlFresh
    ) {
      playingExtendedUrlRef.current = nextExtendedUrl
      playingExtendedExpiryRef.current = extendedClip?.video?.expiryTime
        ? Date.parse(extendedClip.video.expiryTime)
        : 0
      setExtendedUrl(nextExtendedUrl)
    }
    // Surface each clip's wall-clock range for the player caption. `Incident.video` carries no
    // times, so the resolved range is null whenever we fall back to it. Same-value updates keep
    // the previous object so polling completions don't re-render consumers.
    const nextResolvedRange = resolvedClip
      ? { startTime: resolvedClip.startTime, endTime: resolvedClip.endTime }
      : null
    setResolvedClipRange((prev) =>
      prev?.startTime === nextResolvedRange?.startTime &&
      prev?.endTime === nextResolvedRange?.endTime
        ? prev
        : nextResolvedRange
    )
    const nextExtendedRange = extendedClip
      ? { startTime: extendedClip.startTime, endTime: extendedClip.endTime }
      : null
    setExtendedClipRange((prev) =>
      prev?.startTime === nextExtendedRange?.startTime &&
      prev?.endTime === nextExtendedRange?.endTime
        ? prev
        : nextExtendedRange
    )
    if (resolvedVideo) {
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
      onEvent?.(VideoFeedEvent.Received)
      const { url, expiryTime } = resolvedVideo
      if (url && expiryTime) {
        // Swap the player's source only when the clip itself changed or the current signature is
        // about to expire — every poll re-mints the presigned URL for the same object, and
        // swapping restarts playback (the focused clip "blinked" on each extended-clip poll).
        // While kept, the previously armed expiry refetch stands; its completion arrives past the
        // margin and performs the swap.
        const isCurrentUrlFresh =
          playingUrlExpiryRef.current - Date.now() > URL_SWAP_EXPIRY_MARGIN_MS
        if (!isSameClipUrl(playingUrlRef.current, url) || !isCurrentUrlFresh) {
          const expiresTimestamp = Date.parse(expiryTime)
          playingUrlRef.current = url
          playingUrlExpiryRef.current = expiresTimestamp
          // Replace (not stack) the expiry refetch across swaps.
          clearTimeout(timeoutRef.current)
          timeoutRef.current = setTimeout(
            refetch,
            expiresTimestamp - Date.now()
          )
          setUrl(url)
        }
        setIsProcessing(false)
        setError(null)
        if (!isResolvedClipEnabled || extendedClip) {
          clearTimeout(extendedPollTimeoutRef.current)
          extendedPollTimeoutRef.current = null
          setIsExtendedClipPending(false)
          stopPolling()
        } else if (!hasGivenUpOnExtendedRef.current) {
          // The EXTENDED (forensic) clip lands minutes after the incident, so the wait is bounded
          // by the incident's age — not by when its focused clip happens to start playing. An
          // incident opened long after creation is past that window (its extended clip either
          // already exists or never will), so give up immediately instead of spinning for the
          // full timeout. Missing/invalid createdAt falls back to the legacy fixed window.
          const createdAtMs = Date.parse(createdAt ?? '')
          const remainingMs = Number.isNaN(createdAtMs)
            ? extendedFailureTimeoutMs
            : createdAtMs + extendedFailureTimeoutMs - Date.now()
          if (remainingMs <= 0) {
            hasGivenUpOnExtendedRef.current = true
            setIsExtendedClipPending(false)
            stopPolling()
          } else {
            // Keep the query polling at a relaxed cadence until the extended clip arrives or the
            // window closes, surfacing the wait so the Extended tab can show its loading state.
            setIsExtendedClipPending(true)
            startPolling(EXTENDED_CLIP_POLL_INTERVAL)
            if (!extendedPollTimeoutRef.current) {
              extendedPollTimeoutRef.current = setTimeout(() => {
                hasGivenUpOnExtendedRef.current = true
                setIsExtendedClipPending(false)
                stopPolling()
              }, remainingMs)
            }
          }
        } else {
          stopPolling()
        }
      }
    } else if (
      hasVideoClip &&
      !incidentTypesWithoutVideo.includes(incidentType)
    ) {
      startPolling(VIDEO_PROCESSING_POLL_INTERVAL)
      setIsProcessing(true)
      timeoutId = setTimeout(() => {
        stopPolling()
        setIsProcessing(false)
        setIsLoading(false)
        setUrl(null)
        setError(IncidentPlayerError.UNABLE_TO_FIND)
      }, VIDEO_POLL_TIMEOUT)
    } else {
      setUrl(null)
      setError(IncidentPlayerError.NO_INCIDENT_CLIP)
    }
  }

  const reset = () => {
    playingUrlRef.current = null
    playingUrlExpiryRef.current = 0
    playingExtendedUrlRef.current = null
    playingExtendedExpiryRef.current = 0
    setIsExtendedClipPending(false)
  }

  const handleError = () => {
    setIsLoading(false)
    setUrl(null)
    setExtendedUrl(null)
    setResolvedClipRange(null)
    setExtendedClipRange(null)
    // The exposed URLs were cleared — forget them so the next success swaps unconditionally.
    reset()
    setError(IncidentPlayerError.ERROR_FETCHING_INCIDENT_CLIP)
  }

  const { refetch, networkStatus, startPolling, stopPolling } =
    useIncidentVideoQuery({
      variables: { id: incidentId },
      fetchPolicy: 'no-cache',
      skip: !incidentId || shouldUseCannedVideo || skip,
      onError: handleError,
      onCompleted: handleCompleted,
    })

  // Each incident gets a fresh extended-clip polling window and fresh URL bookkeeping; the
  // cleanup also covers unmount.
  useEffect(() => {
    hasGivenUpOnExtendedRef.current = false
    reset()
    return () => {
      // A previous incident's expiry refetch and extended-poll give-up timer must not survive
      // into the next incident (this cleanup also runs on unmount).
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
      clearTimeout(extendedPollTimeoutRef.current)
      extendedPollTimeoutRef.current = null
    }
  }, [incidentId])

  useEffect(() => {
    // Fire on the initial fetch (loading) as well as variable-change refetches
    // (setVariables) so the request->received timing is captured on first drawer
    // open, not only when switching incidents. Apollo reports `ready` (not
    // `loading`) while the query is skipped, so this won't fire for skipped
    // queries. Polling and expiry refetches use other statuses and are excluded.
    if (
      networkStatus === NetworkStatus.loading ||
      networkStatus === NetworkStatus.setVariables
    ) {
      setIsLoading(true)
      onEvent?.(VideoFeedEvent.BeginRequesting)
    }
  }, [networkStatus])

  // ── Terminal clip-failure detection ────────────────────────────────────────
  // v1 detection is a client-side timeout anchored to the incident's age (decided in ENG-30186's
  // QA thread); an explicit per-clip FAILED state from api-general is a future enhancement that
  // would surface these sooner.
  const isExpectingVideo = !incidentTypesWithoutVideo.includes(incidentType)

  const hasFocusedTimeoutElapsed = useClipFailureTimeout({
    createdAt,
    timeoutMs: focusedFailureTimeoutMs,
    isResolved: !!url || !isExpectingVideo,
  })
  // The 30s VIDEO_POLL_TIMEOUT give-up is already terminal and fires far sooner, so honour it too.
  // The timeout is the floor guarantee that no spinner can be indefinite, not the only signal.
  const hasFocusedClipFailed =
    hasFocusedTimeoutElapsed || error === IncidentPlayerError.UNABLE_TO_FIND

  // Extended failure is only meaningful once the focused clip has loaded — if the focused clip
  // itself failed, the toolbar drops the Extended view entirely rather than erroring it.
  const hasExtendedClipFailed = useClipFailureTimeout({
    createdAt,
    timeoutMs: extendedFailureTimeoutMs,
    isResolved: !!extendedUrl || !url,
  })

  return shouldUseCannedVideo
    ? getCannedIncidentVideoStreamState(incidentId)
    : {
        url,
        isLoading,
        errorMessage: error,
        isProcessing,
        extendedUrl,
        isExtendedClipPending,
        resolvedClipRange,
        extendedClipRange,
        hasFocusedClipFailed,
        hasExtendedClipFailed,
      }
}
